import { Checkbox, FormControlLabel, Stack, Typography } from '@mui/material';
import { NotificationPolicyTypeEnum, NotificationType } from '@repo/api-gw-sdk';
import { useState } from 'react';

import type { StepProps } from '@/components/wizard/StepProps';
import { useDAL } from '@/contexts/useDAL';
import { useFeatureFlags } from '@/contexts/useFeatureFlags';

import { supportedNotificationTypes, NotificationTypes } from './data';
import type { UpsertNotificationPolicyState } from './upsertNotificationPolicyFlow';

import { StepContainer } from '../wizard/StepContainer';
import { RenameEntityName } from '../wizard/renameEntityName';

export const NotificationPolicyNotifications = (
  props: StepProps<UpsertNotificationPolicyState>
) => {
  const { isAuditLogNotificationTypeEnabled } = useFeatureFlags();
  const dal = useDAL();
  const [error, setError] = useState<string | undefined>();
  const [saveInProgress, setSaveInProgress] = useState(false);

  return (
    <StepContainer
      hideStepper
      onBackClick={props.back}
      canGoNext={() =>
        !saveInProgress && props.wizardState.notificationTypes?.length > 0
      }
      nextButtonText={
        props.wizardState.id
          ? 'Save'
          : props.wizardState.type === NotificationPolicyTypeEnum.Slack
            ? 'Test and Create'
            : 'Create'
      }
      onNextClick={async () => {
        setSaveInProgress(true);
        setError(undefined);

        try {
          if (props.wizardState.id) {
            await dal.notifications.policies.update(props.wizardState);
            await props.finish();
          } else {
            await dal.notifications.policies.create(props.wizardState);
            await props.finish();
          }
        } catch (e) {
          setError(
            `Failed to save notification policy: ${(e as Error).message}`
          );
        } finally {
          setSaveInProgress(false);
        }
      }}
      navigationComponent={
        error ? (
          <Stack alignItems='center' direction='row' sx={{ color: 'red' }}>
            <i className='ri-error-warning-line mr-[8px] text-[18px]' />
            {error}
          </Stack>
        ) : (
          <RenameEntityName
            label='Notification policy name'
            name={props.wizardState.name}
            onSubmit={(name) =>
              props.setWizardState((state) => ({ ...state, name }))
            }
          />
        )
      }
    >
      <Stack className='flex-grow'>
        <Stack
          className='px-[60px] flex-grow'
          gap='24px'
          color={'var(--mui-palette-text-primary)'}
        >
          <Typography>{`Subscribe to these events:`}</Typography>
          <Stack>
            {supportedNotificationTypes
              .filter(
                (x) =>
                  isAuditLogNotificationTypeEnabled ||
                  x !== NotificationType.AuditLogCreated
              )
              .map((x) => (
                <FormControlLabel
                  key={x}
                  className='w-full'
                  control={
                    <Checkbox
                      sx={{ padding: '10px 10px 10px 30px' }}
                      size='small'
                      checked={props.wizardState.notificationTypes?.includes(x)}
                      onChange={(event, value) =>
                        props.setWizardState((state) => ({
                          ...state,
                          notificationTypes: [
                            ...(state.notificationTypes || []).filter(
                              (y) => y !== x
                            ),
                            ...(value ? [x] : []),
                          ],
                        }))
                      }
                    />
                  }
                  label={NotificationTypes[x]?.title}
                />
              ))}
          </Stack>
        </Stack>
      </Stack>
    </StepContainer>
  );
};
